/**
 * SortableTable Component
 *
 * Drag-and-drop sortable table using @dnd-kit.
 */
import { type ReactNode } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { type Column } from './DataTable';

interface SortableTableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  emptyMessage?: string;
  actions?: (row: T) => ReactNode;
  keyExtractor: (row: T) => string;
  onReorder: (orderedIds: string[]) => void;
}

interface SortableRowProps<T> {
  row: T;
  columns: Column<T>[];
  actions?: (row: T) => ReactNode;
  keyExtractor: (row: T) => string;
}

function SortableRow<T>({ row, columns, actions, keyExtractor }: SortableRowProps<T>) {
  const id = keyExtractor(row);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`bg-white hover:bg-gray-50 transition-colors ${isDragging ? 'shadow-lg' : ''}`}
    >
      {/* Drag handle */}
      <td className="px-2 py-3 w-8">
        <button
          type="button"
          className="p-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
          </svg>
        </button>
      </td>

      {/* Data columns */}
      {columns.map((col) => (
        <td key={String(col.key)} className="px-4 py-3 text-sm text-gray-900">
          {col.render
            ? col.render(row)
            : String((row as Record<string, unknown>)[col.key as string] ?? '-')
          }
        </td>
      ))}

      {/* Actions */}
      {actions && (
        <td className="px-4 py-3 text-right">
          <div className="flex items-center justify-end gap-2">
            {actions(row)}
          </div>
        </td>
      )}
    </tr>
  );
}

export function SortableTable<T>({
  columns,
  data,
  loading = false,
  emptyMessage = 'No data available',
  actions,
  keyExtractor,
  onReorder,
}: SortableTableProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = data.findIndex((item) => keyExtractor(item) === active.id);
      const newIndex = data.findIndex((item) => keyExtractor(item) === over.id);

      // Create new ordered array of IDs
      const ids = data.map(keyExtractor);
      const [movedId] = ids.splice(oldIndex, 1);
      ids.splice(newIndex, 0, movedId);

      onReorder(ids);
    }
  };

  // Skeleton rows for loading state
  const skeletonRows = Array.from({ length: 5 }, (_, i) => i);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-2 py-3 w-8" />
                {columns.map((col) => (
                  <th
                    key={String(col.key)}
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                    style={{ width: col.width }}
                  >
                    {col.header}
                  </th>
                ))}
                {actions && (
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {skeletonRows.map((i) => (
                <tr key={i}>
                  <td className="px-2 py-3">
                    <div className="h-4 w-4 bg-gray-200 rounded animate-pulse" />
                  </td>
                  {columns.map((col) => (
                    <td key={String(col.key)} className="px-4 py-3">
                      <div className="h-4 bg-gray-200 rounded animate-pulse" />
                    </td>
                  ))}
                  {actions && (
                    <td className="px-4 py-3">
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-16 ml-auto" />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
        {emptyMessage}
      </div>
    );
  }

  const ids = data.map(keyExtractor);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-2 py-3 w-8" />
                {columns.map((col) => (
                  <th
                    key={String(col.key)}
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                    style={{ width: col.width }}
                  >
                    {col.header}
                  </th>
                ))}
                {actions && (
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                {data.map((row) => (
                  <SortableRow
                    key={keyExtractor(row)}
                    row={row}
                    columns={columns}
                    actions={actions}
                    keyExtractor={keyExtractor}
                  />
                ))}
              </SortableContext>
            </tbody>
          </table>
        </DndContext>
      </div>
    </div>
  );
}

export default SortableTable;
